// Neighbor flyers (route /vecinos) — v3 (IDC-01, Nov 3, 2026): "Mail it" sends an approved flyer with a print file to a
//   print/mail vendor through the supplier-order flow (ops_mail_it → GM-MARKET-PO draft → send-procurement-rfq quote request).
//   The vendor's quote then lands in Today → Supplier orders for approval, like Mutual's. New status 'mailing' (record 150).
// v2 (CON-TOOLS-02, Oct 29, 2026). Owner: re-org the page by task.
//   ?view=approve   (default) requests waiting for a route, then approved ones to print and mail
//   ?view=history   everything Ops already decided: approved, printing, mailed, cancelled (ops_vecinos_history, migration 64)
//   ?view=templates the 4 flyer templates (FlyerTemplatesAdmin)
// A row opens the side panel (same pattern as Territories): flyer preview, details, and the actions for that status.
// v1 (FLY-01 Phase 3, Oct 20): contractors' free neighbor flyers paid with puntos. Every write is an ops_* function that checks
// the operator's role and logs to the change log. English (ops); the "why this route" note is Spanish because he reads it.
import { useCallback, useEffect, useMemo, useState, type KeyboardEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { canApply, ops, type PrintVendor } from "../lib/ops";
import { useOperator, useToast } from "../lib/context";
import { Chip, Loading, Notice, PageFrame, SectionLabel } from "../components/ui";
import { Panel } from "../components/Panel";
import VecinosFlyer, { type VecinosFlyerReq } from "../components/VecinosFlyer";
import FlyerTemplatesAdmin from "../components/FlyerTemplatesAdmin";

type Status = "requested" | "approved" | "printing" | "mailing" | "mailed" | "cancelled";
interface MailInfo { mail_order_id: number; status: string; po_number: string | null; po_status: string | null; procurement_order_id: string | null;
  vendor: string | null; pieces: number; est_cost_cents: number | null; actual_cost_cents: number | null; test_mode: boolean; submitted_at: string | null }
interface Consent { photo_ok: boolean; credit_ok: boolean; credit: string | null; note?: string }
interface Req extends VecinosFlyerReq {
  request_id: number; status: Status; target: "around_job" | "zip" | "other_area"; maestro: string;
  job_id: string | null; zip: string | null; campaign_id: string | null; mail_date: string | null;
  photo_consent?: Consent | null; before_after_photos?: number;
  requested_at?: string | null; updated_at?: string | null; ops_note?: string | null;
  mail?: MailInfo | null;   // IDC-01 (record 157)
}
interface RouteOpt { distance_m?: number; zip: string; route: string; homes: number; is_job_route: boolean; available: boolean; big_enough: boolean; next_available: string; est_cost_cents: number }
type View = "approve" | "history" | "templates";
type HistFilter = "all" | "approved" | "mailed" | "cancelled";

const money = (c: number) => `$${(c / 100).toFixed(2)}`;
const day = (iso?: string | null) => (iso ? new Date(iso.length === 10 ? `${iso}T12:00:00` : iso).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—");
const ago = (iso?: string | null) => {
  if (!iso) return ""; const h = Math.round((Date.now() - new Date(iso).getTime()) / 3_600_000);
  return h < 1 ? "just now" : h < 24 ? `${h} h ago` : `${Math.round(h / 24)} d ago`;
};
const nextTuesday = () => { const d = new Date(); d.setDate(d.getDate() + ((9 - d.getDay()) % 7 || 7) + 7); return d.toISOString().slice(0, 10); };
const selling = (r: Req) => {
  const k = r.product_key; if (!k || k === "own") return "His services";
  const s = k.split(":")[1] ?? k; return (s.charAt(0).toUpperCase() + s.slice(1)).replace(/-/g, " ").replace("Concreto", "Concrete");
};
const where = (r: Req) => r.target === "around_job" ? `Around his job · ${r.zip ?? "?"}` : r.target === "other_area" ? "Other area (map pin)" : `ZIP ${r.zip ?? "?"}`;
const STATUS_LABEL: Record<Status, string> = { requested: "Waiting", approved: "Approved", printing: "Printing", mailing: "With vendor", mailed: "Mailed", cancelled: "Cancelled" };
const MAIL_STEP: Record<string, string> = { submitted: "quote requested", in_production: "approved, in production", at_post_office: "at the Post Office",
  mailed: "mailed", delivered: "delivered", cancelled: "cancelled", failed: "failed", draft: "draft" };
const tone = (s: Status) => (s === "requested" ? "warn" : s === "cancelled" ? "muted" : "info") as "warn" | "muted" | "info";

async function rpc<T>(fn: string, args: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.rpc(fn, args);
  if (error) throw new Error(error.message);
  return data as T;
}

export default function Vecinos() {
  const { role } = useOperator();
  const allowed = canApply(role);
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const view: View = params.get("view") === "history" ? "history" : params.get("view") === "templates" ? "templates" : "approve";
  const go = (v: View) => { const n = new URLSearchParams(); if (v !== "approve") n.set("view", v); setParams(n, { replace: true }); setSel(null); };

  const [reqs, setReqs] = useState<Req[] | null>(null);
  const [hist, setHist] = useState<Req[] | null>(null);
  const [histErr, setHistErr] = useState("");
  const [error, setError] = useState("");
  const [liab, setLiab] = useState<{ total_puntos: number; routes_equivalent: number; est_cost_cents: number } | null>(null);
  const [sel, setSel] = useState<{ id: number; from: "open" | "history" } | null>(null);
  const [hf, setHf] = useState<HistFilter>("all");
  const [q, setQ] = useState("");

  const refresh = useCallback(async () => {
    try { setReqs(await rpc<Req[]>("ops_vecinos_queue_v2", {})); setError(""); } catch (e) { setError((e as Error).message); }
    try { setLiab(await rpc("ops_puntos_liability", {})); } catch { /* viewer without rights: hide */ }
    try { setHist(await rpc<Req[]>("ops_vecinos_history", { p_limit: 300 })); setHistErr(""); }
    catch (e) { setHist([]); setHistErr((e as Error).message.includes("ops_vecinos_history") ? "History needs database record 64 (ops_vecinos_history)." : (e as Error).message); }
  }, []);
  useEffect(() => { void refresh(); }, [refresh]);

  const waiting = useMemo(() => (reqs ?? []).filter((r) => r.status === "requested"), [reqs]);
  const inFlight = useMemo(() => (reqs ?? []).filter((r) => r.status !== "requested"), [reqs]);
  const histShown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (hist ?? []).filter((r) =>
      (hf === "all" || (hf === "approved" ? r.status === "approved" || r.status === "printing" || r.status === "mailing" : r.status === hf))
      && (!s || r.maestro.toLowerCase().includes(s) || String(r.request_id) === s.replace("#", "") || (r.company_name ?? "").toLowerCase().includes(s)));
  }, [hist, hf, q]);

  const selected = sel ? (sel.from === "open" ? reqs : hist)?.find((r) => r.request_id === sel.id) ?? null : null;
  const panel = selected ? (
    <ReqPanel key={`${sel!.from}-${selected.request_id}`} r={selected} allowed={allowed && sel!.from === "open"}
      onClose={() => setSel(null)} onDone={async (msg) => { toast(msg); setSel(null); await refresh(); }} />
  ) : undefined;

  const rowProps = (r: Req, from: "open" | "history") => ({
    className: sel?.id === r.request_id && sel.from === from ? "row-link selected" : "row-link", tabIndex: 0,
    onClick: () => setSel({ id: r.request_id, from }),
    onKeyDown: (e: KeyboardEvent) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSel({ id: r.request_id, from }); } },
    "aria-label": `Open request ${r.request_id} from ${r.maestro}`,
  });

  return (
    <PageFrame title="Neighbor flyers" lede="Contractors spend puntos on flyers with their name. Approve a route, print, mail, and keep a record." panel={panel}>
      <div className="toolbar">
        <div className="segmented" role="group" aria-label="What do you want to do?">
          <button type="button" className={view === "approve" ? "seg on" : "seg"} aria-pressed={view === "approve"} onClick={() => go("approve")}>
            Approve requests{reqs ? ` (${waiting.length})` : ""}
          </button>
          <button type="button" className={view === "history" ? "seg on" : "seg"} aria-pressed={view === "history"} onClick={() => go("history")}>
            History{hist && !histErr ? ` (${hist.length})` : ""}
          </button>
          <button type="button" className={view === "templates" ? "seg on" : "seg"} aria-pressed={view === "templates"} onClick={() => go("templates")}>
            Flyer templates
          </button>
        </div>
      </div>
      {error && <Notice kind="error">{error}</Notice>}

      {view === "approve" && (
        <>
          {!reqs && !error && <Loading what="requests" />}
          {reqs && (
            <>
              <SectionLabel>Waiting for a route ({waiting.length})</SectionLabel>
              {waiting.length === 0 ? <p className="empty-state">All caught up. No requests waiting.</p> : (
                <div className="table-scroll">
                  <table className="data-table">
                    <thead><tr><th scope="col">Requested</th><th scope="col">Contractor</th><th scope="col">Selling</th><th scope="col">Where</th><th scope="col" className="num">Request</th></tr></thead>
                    <tbody>
                      {waiting.map((r) => (
                        <tr key={r.request_id} {...rowProps(r, "open")}>
                          <td data-label="Requested"><span className="cell-main">{day(r.requested_at)}</span><span className="cell-sub">{ago(r.requested_at)}</span></td>
                          <td data-label="Contractor"><span className="cell-main">{r.maestro}</span><span className="cell-sub">{r.company_name ?? ""}</span></td>
                          <td data-label="Selling">{selling(r)}</td>
                          <td data-label="Where">{where(r)}{r.voice_note_path ? " · voice note" : ""}</td>
                          <td data-label="Request" className="num mono">#{r.request_id}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {inFlight.length > 0 && (
                <>
                  <SectionLabel>Approved: print and mail ({inFlight.length})</SectionLabel>
                  <div className="table-scroll">
                    <table className="data-table">
                      <thead><tr><th scope="col">Mails</th><th scope="col">Contractor</th><th scope="col">Route</th><th scope="col">Status</th><th scope="col">Print file</th></tr></thead>
                      <tbody>
                        {inFlight.map((r) => (
                          <tr key={r.request_id} {...rowProps(r, "open")}>
                            <td data-label="Mails">{day(r.mail_date)}</td>
                            <td data-label="Contractor"><span className="cell-main">{r.maestro}</span><span className="cell-sub">#{r.request_id} · {selling(r)}</span></td>
                            <td data-label="Route" className="mono">{r.zip} {r.route ?? ""}</td>
                            <td data-label="Status"><Chip tone={tone(r.status)}>{STATUS_LABEL[r.status]}</Chip></td>
                            <td data-label="Print file">{r.mail?.po_number ? <span className="mono">{r.mail.po_number}{r.mail.test_mode ? " (test)" : ""}</span> : r.print_pdf_path ? "Ready" : <span className="muted">Not made yet</span>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
              {liab && <p className="hint">Puntos not yet spent (all contractors): <strong>{liab.total_puntos.toLocaleString()}</strong> ≈ {liab.routes_equivalent} routes ≈ {money(liab.est_cost_cents)} if all were mailed.</p>}
            </>
          )}
        </>
      )}

      {view === "history" && (
        <>
          <div className="toolbar">
            <div className="segmented" role="group" aria-label="Show">
              {(["all", "approved", "mailed", "cancelled"] as HistFilter[]).map((f) => (
                <button key={f} type="button" className={hf === f ? "seg on" : "seg"} aria-pressed={hf === f} onClick={() => setHf(f)}>
                  {f === "all" ? "All" : f === "approved" ? "Approved" : f === "mailed" ? "Mailed" : "Cancelled"}
                </button>
              ))}
            </div>
            <div className="toolbar-actions">
              <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search contractor or #" aria-label="Search contractor or request number" />
            </div>
          </div>
          {histErr && <Notice kind="error">{histErr}</Notice>}
          {!hist && <Loading what="history" />}
          {hist && !histErr && histShown.length === 0 && <p className="empty-state">{hist.length ? "Nothing matches." : "No decisions yet."}</p>}
          {histShown.length > 0 && (
            <div className="table-scroll">
              <table className="data-table">
                <thead><tr><th scope="col">Last change</th><th scope="col">Contractor</th><th scope="col">Status</th><th scope="col">Route</th><th scope="col">Note</th></tr></thead>
                <tbody>
                  {histShown.map((r) => (
                    <tr key={r.request_id} {...rowProps(r, "history")}>
                      <td data-label="Last change">{day(r.updated_at)}</td>
                      <td data-label="Contractor"><span className="cell-main">{r.maestro}</span><span className="cell-sub">#{r.request_id} · {selling(r)}</span></td>
                      <td data-label="Status"><Chip tone={tone(r.status)}>{STATUS_LABEL[r.status]}</Chip></td>
                      <td data-label="Route" className="mono">{r.route ? `${r.zip} ${r.route} · ${day(r.mail_date)}` : <span className="muted">—</span>}</td>
                      <td data-label="Note"><span className="cell-sub" style={{ maxWidth: 360, display: "inline-block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {r.status === "cancelled" ? r.ops_note ?? "" : r.ops_route_note ?? r.ops_note ?? ""}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {view === "templates" && (
        <>
          <p className="hint">The four flyer designs contractors choose from. Headlines print in English; the Spanish line only shows in the contractor's app.</p>
          <FlyerTemplatesAdmin allowed={allowed} />
        </>
      )}
    </PageFrame>
  );
}

// ── IDC-01: Mail it ──────────────────────────────────────────
function MailItCard({ r, onDone }: { r: Req; onDone: (msg: string) => Promise<void> }) {
  const [vendors, setVendors] = useState<PrintVendor[] | null>(null);
  const [vendor, setVendor] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    ops.printVendors().then((v) => { setVendors(v); if (v.length === 1) setVendor(v[0].supplier_id); }).catch((e: Error) => setErr(e.message));
  }, []);
  const chosen = vendors?.find((v) => v.supplier_id === vendor) ?? null;
  async function mailIt() {
    if (busy || !vendor) return; setBusy(true); setErr("");
    try {
      const built = await ops.mailIt("vecinos", String(r.request_id), vendor);
      const sent = await ops.sendRfq(built.order_id);
      await onDone(`Quote request sent to ${sent.sent_to}${sent.test_mode ? " (TEST)" : ""} · ${built.po_number}`);
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); }
  }
  return (
    <div className="editor-section card">
      <SectionLabel>Mail it</SectionLabel>
      {err && <Notice kind="error">{err}</Notice>}
      {!vendors && !err && <Loading what="print vendors" />}
      {vendors && vendors.length === 0 && <p className="hint">No print/mail vendor is set up yet. Add one first (Supplier orders).</p>}
      {vendors && vendors.length > 0 && (
        <>
          <div className="row-actions" style={{ alignItems: "center" }}>
            <select value={vendor} onChange={(e) => setVendor(e.target.value)} aria-label="Print vendor">
              <option value="">Choose a vendor…</option>
              {vendors.map((v) => <option key={v.supplier_id} value={v.supplier_id}>{v.name}{v.test_mode ? " (test mode)" : ""}</option>)}
            </select>
            <button type="button" className="btn primary" disabled={busy || !vendor} onClick={() => void mailIt()}>
              {busy ? "Sending…" : "Mail it: request a quote"}</button>
          </div>
          <p className="hint">Builds a print order (PO GM-MARKET-PO-…) and emails the vendor a quote request with the print file link.
            {chosen?.test_mode ? " This vendor is in test mode: the email goes only to the test address." : ""} Checks the monthly Neighbor-flyer mail budget first.</p>
        </>
      )}
    </div>
  );
}

// ── Request panel ───────────────────────────────────────────

function ReqPanel({ r, allowed, onClose, onDone }: { r: Req; allowed: boolean; onClose: () => void; onDone: (msg: string) => Promise<void> }) {
  const toast = useToast();
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [mailDate, setMailDate] = useState(nextTuesday());
  const [routes, setRoutes] = useState<RouteOpt[] | null>(null);
  const [why, setWhy] = useState("");
  const [whyEn, setWhyEn] = useState<string | null>(null);
  const [xl, setXl] = useState(false);
  const [note, setNote] = useState("");
  const [cost, setCost] = useState("");
  const [more, setMore] = useState<"consent" | "cancel" | null>(null);
  const [making, setMaking] = useState(false);   // CON-TOOLS-03: "Make print file" (gm-vecinos-print)
  async function makePrint() {
    if (making) return; setMaking(true); setErr("");
    try {
      const { data, error } = await supabase.functions.invoke("gm-vecinos-print", { body: { action: "make", request_id: r.request_id } });
      if (error) {
        let msg = error.message;
        try { const b = await (error as { context?: Response }).context?.json(); if (b?.message) msg = b.message; else if (b?.error) msg = b.error; } catch { /* keep */ }
        throw new Error(msg);
      }
      await onDone(`Print file made${data?.bytes ? ` (${Math.round(data.bytes / 1024)} KB)` : ""}`);
    } catch (e) { setErr(`Print file not made: ${(e as Error).message}`); }
    finally { setMaking(false); }
  }
  const [reason, setReason] = useState("");
  const [consentText, setConsentText] = useState("");
  const [first, setFirst] = useState(""); const [hood, setHood] = useState("");

  const loadRoutes = useCallback(async (date: string) => {
    setRoutes(null);
    try { setRoutes(await rpc<RouteOpt[]>("ops_vecinos_suggest_routes_v2", { p_request: r.request_id, p_mail_date: date })); } catch (e) { setErr((e as Error).message); }
  }, [r.request_id]);
  useEffect(() => { if (allowed && r.status === "requested") void loadRoutes(mailDate); }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  async function act(fn: () => Promise<unknown>, done: string) {
    if (busy) return; setBusy(true); setErr("");
    try { await fn(); await onDone(done); } catch (e) { setErr((e as Error).message); } finally { setBusy(false); }
  }
  const translateWhy = async () => {
    const text = why.trim(); if (text.length < 3 || xl) return;
    setXl(true);
    try {
      const { data, error } = await supabase.functions.invoke("gm-ops-translate", { body: { action: "route_note", request_id: r.request_id, text } });
      if (error || !data?.spanish) throw new Error(data?.error ?? "translate_failed");
      setWhyEn(text); setWhy(data.spanish);
    } catch { toast("Couldn't translate. Try again, or write it in Spanish."); }
    finally { setXl(false); }
  };

  return (
    <Panel kicker={`Request #${r.request_id} · ${STATUS_LABEL[r.status]}`} title={`${r.maestro}${r.company_name ? ` · ${r.company_name}` : ""}`} onClose={onClose}>
      {err && <Notice kind="error">{err}</Notice>}
      <p className="hint" style={{ marginTop: 0 }}>
        {selling(r)} · {where(r)} · requested {day(r.requested_at)}
        {r.campaign_id ? ` · ${r.campaign_id}` : ""}{r.mail_date ? ` · mails ${day(r.mail_date)}` : ""}
      </p>
      <VecinosFlyer r={r} />
      {r.job_id && (
        <p className="hint">Photos: {r.photo_consent?.photo_ok ? `homeowner OK${r.photo_consent.credit ? ` · credit “${r.photo_consent.credit}”` : " · no name"}` : "no consent yet → stock photo"}
          {r.before_after_photos ? ` · before/after photos: ${r.before_after_photos}` : ""}</p>
      )}
      {r.status === "cancelled" && r.ops_note && <p className="hint">Cancelled: “{r.ops_note}” · puntos refunded.</p>}
      {r.status !== "cancelled" && r.status !== "requested" && r.ops_note && <p className="hint">Internal note: “{r.ops_note}”</p>}

      {allowed && r.status === "requested" && (
        <div className="editor-section card">
          <SectionLabel>1 · Why this route (he reads this)</SectionLabel>
          <div style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
            <input value={why} onChange={(e) => setWhy(e.target.value)} maxLength={400} style={{ flex: 1, minWidth: 0 }} placeholder="Write in English, then Translate to Spanish" />
            <button type="button" className="btn" disabled={xl || why.trim().length < 3} onClick={() => void translateWhy()}>{xl ? "Translating…" : "Translate to Spanish"}</button>
          </div>
          {whyEn && <p className="hint" style={{ margin: "4px 0 0" }}>Your English: “{whyEn}”. Check the Spanish above.</p>}
          <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Internal note (optional, Ops only)" style={{ marginTop: 8, width: "100%" }} />

          <SectionLabel>2 · Mail date and route</SectionLabel>
          <label>Mail date <input type="date" value={mailDate} onChange={(e) => { setMailDate(e.target.value); void loadRoutes(e.target.value); }} /></label>
          {!routes && <Loading what="routes" />}
          {routes && routes.length === 0 && <p className="hint">{r.target === "other_area" ? "No route shapes near the pin yet: load them with ingest-route-geoms for that ZIP, then reopen." : "No routes for this date."}</p>}
          {routes?.map((o) => {
            const ok = o.available && o.big_enough;
            return (
              <div key={o.route} className="row-actions" style={{ alignItems: "center", marginTop: 8 }}>
                <span style={{ flex: 1, minWidth: 200 }}><strong>{o.zip} {o.route}</strong> · {o.homes} homes · {money(o.est_cost_cents)}{o.is_job_route ? " · his job's route" : ""}{o.distance_m != null ? ` · ${(o.distance_m / 1609).toFixed(1)} mi from pin` : ""}</span>
                {!o.available && <Chip tone="muted">next {o.next_available}</Chip>}
                {o.available && !o.big_enough && <Chip tone="muted">under 450 homes</Chip>}
                <button type="button" className="btn primary" disabled={!ok || busy || why.trim().length < 8} title={why.trim().length < 8 ? "Write why this route first" : undefined}
                  onClick={() => act(() => rpc("ops_vecinos_approve_v2", { p_request: r.request_id, p_zip: o.zip, p_route: o.route, p_mail_date: mailDate, p_route_note: why, p_note: note }), `Approved ${o.zip} ${o.route} for ${day(mailDate)}`)}>Approve</button>
              </div>
            );
          })}
        </div>
      )}

      {r.status === "mailing" && r.mail && (
        <div className="editor-section card">
          <SectionLabel>With the print vendor</SectionLabel>
          {r.mail.test_mode && <Notice kind="info">TEST order: emails go only to the test address.</Notice>}
          <dl className="facts">
            <dt>PO</dt><dd className="mono">{r.mail.po_number ?? "—"}</dd>
            <dt>Vendor</dt><dd>{r.mail.vendor ?? "—"}</dd>
            <dt>Step</dt><dd>{MAIL_STEP[r.mail.status] ?? r.mail.status}</dd>
            <dt>Pieces</dt><dd>{r.mail.pieces}</dd>
            <dt>Cost</dt><dd>{r.mail.actual_cost_cents != null ? `${money(r.mail.actual_cost_cents)} (approved quote)` : r.mail.est_cost_cents != null ? `about ${money(r.mail.est_cost_cents)} (estimate)` : "—"}</dd>
          </dl>
          <p className="hint">When the vendor replies with a quote, it shows in Today → Supplier orders. Approving it there orders the print run.</p>
        </div>
      )}

      {allowed && (r.status === "approved" || r.status === "printing") && r.print_pdf_path && (
        <MailItCard r={r} onDone={onDone} />
      )}

      {allowed && (r.status === "approved" || r.status === "printing") && (
        <div className="editor-section card">
          <SectionLabel>Print and mail</SectionLabel>
          <div className="row-actions" style={{ alignItems: "center", marginBottom: 10 }}>
            <button type="button" className={r.print_pdf_path ? "btn" : "btn primary"} disabled={making || busy} onClick={() => void makePrint()}>
              {making ? "Making the print file…" : r.print_pdf_path ? "Make it again" : "Make print file"}</button>
            <span className="hint" style={{ margin: 0 }}>{r.print_pdf_path
              ? `Ready${r.print_made_at ? ` · made ${r.print_made_at.slice(0, 16).replace("T", " ")}` : ""}`
              : "Re-prices every line at the route ZIP for the mail date; stops if one fails."}</span>
          </div>
          {r.status === "approved" && (
            <button type="button" className="btn" disabled={busy}
              onClick={() => act(() => rpc("ops_vecinos_set_status", { p_request: r.request_id, p_status: "printing", p_actual_cost_cents: null, p_note: "print order placed" }), "Marked printing")}>Print order placed</button>
          )}
          <div className="row-actions" style={{ marginTop: 8 }}>
            <input value={cost} onChange={(e) => setCost(e.target.value.replace(/[^0-9.]/g, ""))} placeholder="Invoice: print + postage, $" inputMode="decimal" />
            <button type="button" className="btn primary" disabled={busy}
              onClick={() => act(() => rpc("ops_vecinos_set_status", { p_request: r.request_id, p_status: "mailed", p_actual_cost_cents: cost ? Math.round(parseFloat(cost) * 100) : null, p_note: "mailed" }), "Marked mailed")}>Mark mailed</button>
          </div>
        </div>
      )}

      {allowed && (r.status === "requested" || r.status === "approved" || r.status === "printing") && (
        <div style={{ marginTop: 12 }}>
          <div className="row-actions">
            {r.job_id && !r.photo_consent?.photo_ok && <button type="button" className="btn quiet" aria-expanded={more === "consent"} onClick={() => setMore(more === "consent" ? null : "consent")}>Record texted photo consent…</button>}
            <button type="button" className="btn quiet" aria-expanded={more === "cancel"} onClick={() => setMore(more === "cancel" ? null : "cancel")}>Cancel and refund…</button>
          </div>
          {more === "consent" && r.job_id && (
            <div className="editor-section card">
              <SectionLabel>Homeowner's texted reply</SectionLabel>
              <textarea value={consentText} onChange={(e) => setConsentText(e.target.value)} rows={2} placeholder="Paste the homeowner's reply (e.g. YES)" style={{ width: "100%" }} />
              <div className="row-actions" style={{ marginTop: 8 }}>
                <input value={first} onChange={(e) => setFirst(e.target.value)} placeholder="First name (optional)" />
                <input value={hood} onChange={(e) => setHood(e.target.value)} placeholder="Neighborhood (optional)" />
              </div>
              <div className="row-actions" style={{ marginTop: 8 }}>
                <button type="button" className="btn primary" disabled={busy || consentText.trim().length < 10}
                  onClick={() => act(() => rpc("ops_record_photo_consent", { p_job: r.job_id, p_photo_ok: true, p_credit_ok: !!(first.trim() && hood.trim()), p_first_name: first, p_neighborhood: hood, p_channel: "sms", p_consent_text: consentText }), "Consent recorded")}>Record: photos OK</button>
                <button type="button" className="btn" disabled={busy || consentText.trim().length < 10}
                  onClick={() => act(() => rpc("ops_record_photo_consent", { p_job: r.job_id, p_photo_ok: false, p_credit_ok: false, p_first_name: null, p_neighborhood: null, p_channel: "sms", p_consent_text: consentText }), "Recorded: no photos")}>Record: no photos</button>
              </div>
            </div>
          )}
          {more === "cancel" && (
            <div className="editor-section card">
              <SectionLabel>Cancel and refund the puntos</SectionLabel>
              <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required, 5+ characters)" style={{ width: "100%" }} />
              <button type="button" className="btn" disabled={busy || reason.trim().length < 5} style={{ marginTop: 8 }}
                onClick={() => act(() => rpc("ops_cancel_vecinos_request", { p_request: r.request_id, p_reason: reason }), "Cancelled, puntos refunded")}>Cancel request</button>
            </div>
          )}
        </div>
      )}
    </Panel>
  );
}
